import { cn, getVideoEmbedInfo } from '../../../../utils';
import { ContentDescription } from '../shared/content-description';
import type { ContentDisplayVariant } from '../../hooks/content-renderer-types';
import type { StudentVideoContent } from '../../hooks/use-class-blocks';
import { renderInlineMarkdown } from '../../../custom/markdown';

interface VideoContentDisplayProps {
  content: StudentVideoContent;
  variant?: ContentDisplayVariant;
}

export function VideoContentDisplay({ content, variant = 'card' }: VideoContentDisplayProps) {
  if (!content.url) {
    return null;
  }

  const embedInfo = getVideoEmbedInfo(content.url);

  return (
    <div
      className={cn(
        'flex',
        variant === 'inline' ? 'justify-start' : 'justify-center'
      )}
    >
      <figure className={cn('space-y-2 w-full', variant !== 'inline' && 'max-w-4xl')}>
        <div className="w-full overflow-hidden rounded-lg border border-border shadow-sm bg-muted">
          {embedInfo ? (
            <div className="aspect-video">
              <iframe
                src={embedInfo.embedUrl}
                title={content.title ?? 'Embedded video'}
                className="h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <video controls className="w-full" src={content.url}>
              Your browser does not support the video tag.
            </video>
          )}
        </div>
        {(content.title || content.description) && (
          <figcaption className="space-y-1 text-center">
            {content.title && (
              <span className="text-sm font-medium text-muted-foreground">
                {renderInlineMarkdown(content.title, 'video-content-title')}
              </span>
            )}
            <ContentDescription
              as="p"
              text={content.description}
              markdown
              className="m-0 text-center"
            />
          </figcaption>
        )}
      </figure>
    </div>
  );
}
